import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { KeyValue } from "models/utilTypes/keyValue";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import {
  DashboardButtonGroup,
  DetailsTableViewSmall,
  DialogActionButton,
  LoadingWithLogo,
  PageLayouts,
  RouteBreadCrumb,
  SimpleTooltip,
  Skeletons,
  StaticInfoMessage,
  StyledButton,
  ViewPanel,
} from "components/common";
import CopyButton from "components/common/buttons/copyButton";
import { Grid } from "@mui/material";
import { useParams } from "react-router-dom";
import { ErrorView } from "components/error";
import useAdvisorPermissions from "../usePermissions";
import MonthlyReportStatusTag, { isSharedWithCustomer } from "./statusTag";
import MonthlyReportBody from "./reportBody";
import MonthlyReportApproveFooter from "./approveFooter";
import {
  useEditMonthlyReportDialog,
  useRegenerateMonthlyReportDialog,
  useUnpublishMonthlyReportDialog,
} from "./dialogs";
import { useMonthlyReportService } from "serviceHooks";
//#endregion
import { GENERATING_POLL_INTERVAL_MS, GENERATING_POLL_TIMEOUT_MS } from "./polling";
import { toRetryRequest } from "./reportPeriod";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

/**
 * A single monthly report — the advisor's working surface.
 *
 * entity-page / Document-detail per the web guidelines: the report itself occupies the document
 * column and the metadata panels sit beside it. The iteration actions live in the top-right
 * DashboardButtonGroup; approval sits alone in the PageFooter, which renders as a centred
 * floating button (`position: sticky; width: fit-content; margin: 0 auto`) — the same shape as
 * the Save action on a settings form.
 *
 * Separating the two is deliberate rather than cosmetic. Approval publishes a customer case and
 * Core notifies the customer, and that cannot be walked back; keeping it out of reach of the
 * iterate buttons is what stops a stray click from sending a half-finished report.
 */
const MonthlyReportDetailView: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const { advisorViewsAvailable } = useAdvisorPermissions();
  const companyId = useSelector((state: AppState) => state.auth.selectedCompany.id);
  const { getAdvisorReport, generateReport } = useMonthlyReportService();

  const [report, setReport] = React.useState<WintSuperkollAPIModel.MonthlyReport>();
  const [loading, setLoading] = React.useState<boolean>(true);

  const load = React.useCallback(() => {
    return (
      getAdvisorReport(id)
        .then(setReport)
        // A failed poll deliberately leaves the report alone rather than recording the failure. The
        // view falls back to the error below only while there is nothing to show at all, so the one
        // request that matters is the first. Polling every five seconds makes a transient blip
        // likely, and a remembered failure would strand a perfectly good report on the error view
        // until the page was reloaded.
        .catch(() => undefined)
        .finally(() => setLoading(false))
    );
    // Deps are `[id]` only: `getAdvisorReport` is fresh each render, so including it would loop.
  }, [id]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Generation runs in the report service's background and takes 15-90 seconds, so the view polls
  // until the status leaves Generating rather than asking the advisor to refresh the page. It gives
  // up after GENERATING_POLL_TIMEOUT_MS and says so, rather than polling a stuck pass forever.
  const isGenerating = report?.status === WintSuperkollAPIModel.MonthlyReportStatus.Generating;
  const [pollTimedOut, setPollTimedOut] = React.useState<boolean>(false);
  React.useEffect(() => {
    if (!isGenerating) return undefined;

    // Measured from when this view started waiting, not from when the pass began: a report that has
    // been stuck since yesterday still deserves five minutes of this tab's attention before the
    // advisor is told to reload.
    const startedWaitingAt = Date.now();
    const timer = window.setInterval(() => {
      if (Date.now() - startedWaitingAt >= GENERATING_POLL_TIMEOUT_MS) {
        window.clearInterval(timer);
        setPollTimedOut(true);
        return;
      }
      load();
    }, GENERATING_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [isGenerating, load]);

  // Regenerate has no server-side "working" status to poll — the endpoint simply doesn't answer
  // until the pass is done — so the wait is tracked here instead.
  const [regenerating, setRegenerating] = React.useState<boolean>(false);
  const trackRegenerate = React.useCallback(
    (call: Promise<unknown>) => {
      setRegenerating(true);
      call
        .then(() => load())
        // The shared error interceptor has already shown the failure; this only stops the
        // rejection from going unhandled.
        .catch(() => undefined)
        .finally(() => setRegenerating(false));
    },
    [load]
  );

  // A failed pass is retried by generating the period again, not by regenerating: the service
  // refuses regenerate and markdown edits on a Failed row because there is no first pass to build
  // on, and answers a fresh generate for the same period by resuming that very row rather than
  // creating a second one. It reports the resumed row as Generating from its queue straight away,
  // so a reload here is enough to put the view back into its ordinary wait.
  const [retrying, setRetrying] = React.useState<boolean>(false);
  const retryGeneration = React.useCallback(
    (request: WintSuperkollAPIModel.GenerateMonthlyReportRequest) => {
      setRetrying(true);
      generateReport(request)
        .then(() => load())
        // The shared error interceptor has already shown the failure; this only stops the
        // rejection from going unhandled.
        .catch(() => undefined)
        .finally(() => setRetrying(false));
      // `generateReport` is fresh each render, so including it would loop.
    },
    [load]
  );

  const getRegenerateDialog = useRegenerateMonthlyReportDialog(id, trackRegenerate);
  const getEditDialog = useEditMonthlyReportDialog(id, report?.currentMarkdown);
  const getUnpublishDialog = useUnpublishMonthlyReportDialog(id);

  if (loading) return <LoadingWithLogo />;
  if (!report) return <ErrorView />;

  const isBusy = isGenerating || regenerating || retrying;
  // Regenerating is awaited in the browser, so it cannot go stale the way a polled generation can.
  const busyNotice = regenerating
    ? { displayType: StyleEnums.DisplayType.Info, text: TranslationKeys.Advisor.MonthlyReportRegenerating }
    : pollTimedOut
      ? { displayType: StyleEnums.DisplayType.Warning, text: TranslationKeys.Advisor.MonthlyReportGeneratingStalled }
      : { displayType: StyleEnums.DisplayType.Info, text: TranslationKeys.Advisor.MonthlyReportGenerating };
  const isShared = isSharedWithCustomer(report.status);
  const isFailed = report.status === WintSuperkollAPIModel.MonthlyReportStatus.Failed;
  const isDraft = report.status === WintSuperkollAPIModel.MonthlyReportStatus.Draft;
  const period = `${DateHelper.convertDateToShortISOString(
    report.periodStart
  )} – ${DateHelper.convertDateToShortISOString(report.periodEnd)}`;
  // The company-scoped shape is the current one: the report endpoint only answers once a company is
  // selected, and naming the company in the link lands the customer straight on the report instead
  // of a picker. `/MonthlyReports/{id}` still resolves, but only as the fallback kept for links
  // mailed before the id was included — see routing/monthlyReport.tsx. Absolute, because this is
  // copied out of the app and pasted somewhere else, and the origin differs per environment.
  const customerLink = `${window.location.origin}/MonthlyReports/${companyId}/${report.id}`;

  // The period as the generate endpoint wants it, taken back off the report rather than held in
  // state: its last calendar month and month count are the whole identity of a report, and reading
  // both off the row is what makes the retry land on this report instead of creating a neighbour —
  // a failed quarter retried as its first month would be a different report.
  const retryRequest = toRetryRequest(
    DateHelper.convertDateToShortISOString(report.periodStart),
    DateHelper.convertDateToShortISOString(report.periodEnd)
  );

  const buttons = isFailed
    ? [
        <StyledButton size="sm" onClick={() => retryGeneration(retryRequest)} testId="retryMonthlyReport" key="t">
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.RetryMonthlyReport)}
        </StyledButton>,
      ]
    : isShared
      ? [
          <DialogActionButton
            size="sm"
            actionDialog={getUnpublishDialog}
            reload={load}
            testId="unpublishReport"
            key="u"
          >
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.UnpublishMonthlyReport)}
          </DialogActionButton>,
        ]
      : // Draft explicitly, not "everything else". Regenerate and edit both need a completed first
        // pass, so the service refuses them on anything but a draft, and the one status that would
        // otherwise land here is Unknown: a state the report service reports and this API does not
        // recognise yet. Offering it two actions that are certain to be refused is how a reviewer
        // ends up filing a bug against a status nobody has defined.
        !isDraft
        ? []
        : [
            <DialogActionButton
              size="sm"
              actionDialog={getRegenerateDialog}
              reload={load}
              testId="regenerateReport"
              key="r"
            >
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.Regenerate)}
            </DialogActionButton>,
            <DialogActionButton size="sm" actionDialog={getEditDialog} reload={load} testId="editReport" key="e">
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit)}
            </DialogActionButton>,
          ];

  return (
    <PageLayouts.DashboardLayout
      entityKey={0}
      hasPermissions={advisorViewsAvailable}
      title={period}
      // A `:id` segment never matches a route config entry, so the breadcrumb can't name the leaf
      // on its own — every :id view supplies `current` itself. Without it the trail ends in "/".
      breadCrumb={<RouteBreadCrumb className={cssExports.dashBoardNavigation} current={period} />}
      topRightButtons={
        // While a pass is running there is nothing to iterate on or approve yet, and a status with
        // no applicable actions leaves the slot empty rather than an empty button group.
        isBusy || !buttons.length ? undefined : <DashboardButtonGroup buttons={buttons} />
      }
      footer={isDraft && !isBusy ? <MonthlyReportApproveFooter reportId={id} onApproved={load} /> : undefined}
    >
      <Grid container rowGap={3} columnSpacing={3}>
        <Grid item md={8} xs={12}>
          <Grid container direction={"column"} spacing={3}>
            {isBusy && (
              <Grid item>
                <StaticInfoMessage displayType={busyNotice.displayType} fullWidth surface="background">
                  {LocalizationHelperInstance.getFormattedMessage(busyNotice.text)}
                </StaticInfoMessage>
              </Grid>
            )}
            {/* Suppressed while a retry is in flight: the row stays Failed until the service
                picks the attempt off its queue, so leaving this up would pair a red "it could
                not be written" with the blue "it is being written" directly above it. */}
            {isFailed && !isBusy && (
              <Grid item>
                <StaticInfoMessage displayType={StyleEnums.DisplayType.Danger} fullWidth surface="background">
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.MonthlyReportFailed)}
                </StaticInfoMessage>
              </Grid>
            )}
            {isShared && (
              <Grid item>
                <StaticInfoMessage displayType={StyleEnums.DisplayType.Success} fullWidth surface="background">
                  {report.approvedAt
                    ? LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.Advisor.MonthlyReportSharedBanner,
                        "",
                        {
                          date: DateHelper.convertDateToShortISOString(report.approvedAt),
                        }
                      )
                    : LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.Advisor.MonthlyReportSharedBannerNoDate
                      )}
                </StaticInfoMessage>
              </Grid>
            )}
            <Grid item>
              <ViewPanel appearance="lg" gridSize="lg">
                {/* A skeleton in the report's own shape, rather than an empty panel — the reader
                    sees what is being built. */}
                {isBusy ? (
                  <Skeletons.TextSkeleton lines={12} />
                ) : (
                  <MonthlyReportBody html={report.currentRenderedHtml} />
                )}
              </ViewPanel>
            </Grid>
          </Grid>
        </Grid>

        <Grid item md={4} xs={12}>
          <Grid container direction={"column"} spacing={3}>
            <Grid item>
              <ViewPanel
                className={cssExports.advisorMonthlyReportDetailsPanel}
                icon={IconDefinitionConstants.WInfo}
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Details)}
              >
                <DetailsTableViewSmall
                  detailsList={[
                    new KeyValue(
                      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.MonthlyReportPeriod),
                      period
                    ),
                    ACTION_SEPARATOR,
                    new KeyValue(
                      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Status),
                      <MonthlyReportStatusTag status={report.status} />
                    ),
                    ACTION_SEPARATOR,
                    new KeyValue(
                      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.MonthlyReportIteration),
                      // Stringified because DetailsTable skips items with a falsy value, and
                      // iteration 0 is a real value.
                      String(report.currentIteration)
                    ),
                  ]}
                />
              </ViewPanel>
            </Grid>

            {!!report.iterations?.length && (
              <Grid item>
                <ViewPanel
                  icon={IconDefinitionConstants.WVersions}
                  title={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Advisor.MonthlyReportIterations
                  )}
                >
                  <dl className={cssExports.advisorMonthlyReportIterations}>
                    {report.iterations.map((iteration) => (
                      <React.Fragment key={iteration.iterationNumber}>
                        <dt>{iteration.iterationNumber}</dt>
                        <dd>
                          {iteration.kaFeedback ||
                            LocalizationHelperInstance.getFormattedMessage(
                              TranslationKeys.Advisor.MonthlyReportFirstDraft
                            )}
                        </dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </ViewPanel>
              </Grid>
            )}

            {isShared && (
              <Grid item>
                <ViewPanel
                  icon={IconDefinitionConstants.WLink}
                  title={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Advisor.MonthlyReportCustomerLink
                  )}
                >
                  <Grid container direction={"column"} spacing={2}>
                    <Grid item>
                      {/* The link is the button's own label, the way the incoming-invoice mail
                          address is — a separate line plus an unlabelled button renders as an
                          empty control. The span is what carries the tooltip's ref: SimpleTooltip
                          clones its child with a ref, and CopyButton doesn't forward one. */}
                      <SimpleTooltip
                        text={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.CopyLink)}
                      >
                        <span>
                          <CopyButton
                            copyText={customerLink}
                            showCopyIcon
                            className={cssExports.advisorMonthlyReportLink}
                          >
                            {customerLink}
                          </CopyButton>
                        </span>
                      </SimpleTooltip>
                    </Grid>
                    <Grid item>
                      <p className={cssExports.advisorMonthlyReportHelp}>
                        {LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.Advisor.MonthlyReportCustomerLinkHelp
                        )}
                      </p>
                    </Grid>
                  </Grid>
                </ViewPanel>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </PageLayouts.DashboardLayout>
  );
};

export default MonthlyReportDetailView;
